/**
 * Back-pressure for AI coding agents.
 *
 * `cai verify` runs the cheap checks an agent should pass before declaring
 * "done": typecheck, build, drift. Each step is auto-detected from the
 * project's manifest — no config file needed.
 *
 * Exit codes follow Claude Code's hook contract:
 *   0 — everything passed (or skipped because not applicable)
 *   2 — at least one check failed; Claude Code re-engages the agent
 *
 * Output is human-friendly by default, machine-readable with --json so the
 * Stop hook can pipe it back into the agent's context.
 *
 * Design principles:
 *   - Fast: skip slow tests (run a separate `cai verify --full` for those)
 *   - Honest: never silently skip a step the project actually has
 *   - Bounded: each step has a timeout so a runaway script can't hang the hook
 */
import { existsSync, readFileSync } from "node:fs";
import { spawnSync } from "node:child_process";
import { join } from "node:path";
import chalk from "chalk";
import { globSync } from "glob";
import type { CaiConfig, ManifestInfo, ProjectModel } from "./types.js";
import { scanProjectModel } from "./scanner/manifest.js";
import { runDriftCheck } from "./drift/index.js";

export interface VerifyStep {
  name: string;
  command: string;
  status: "passed" | "failed" | "skipped";
  durationMs: number;
  output?: string;
  reason?: string;
}

export interface VerifyResult {
  passed: boolean;
  steps: VerifyStep[];
  totalDurationMs: number;
}

interface VerifyOptions {
  /** Skip drift check (useful when running drift separately) */
  skipDrift?: boolean;
  /** Skip language/tool adapter checks */
  skipAdapters?: boolean;
  /** Run the test suite (the highest-signal but slowest check). Off in the default fast path. */
  runTests?: boolean;
  /** Per-step timeout in ms */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 120_000;

export interface AdapterCheck {
  name: string;
  command: string;
}

/** Run all auto-detected verification steps. */
export async function runVerify(config: CaiConfig, opts: VerifyOptions = {}): Promise<VerifyResult> {
  const start = Date.now();
  const steps: VerifyStep[] = [];
  const project = scanProjectModel(config.projectRoot);
  const timeout = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  // 1. Typecheck — TypeScript projects only
  const typecheckStep = await runTypecheck(config.projectRoot, project, timeout);
  if (typecheckStep) steps.push(typecheckStep);

  // Stop early on hard failure — no point continuing if types are broken
  if (steps.some((s) => s.status === "failed")) {
    return finalize(steps, start);
  }

  // 2. Build — only if a build script exists
  const buildStep = await runBuild(config.projectRoot, project, timeout);
  if (buildStep) steps.push(buildStep);

  if (steps.some((s) => s.status === "failed")) {
    return finalize(steps, start);
  }

  // 3. Language/tool adapters — run only when the project already declares
  // the relevant stack or config. These add signal without making cai own
  // every ecosystem's full verification story.
  if (!opts.skipAdapters) {
    for (const check of detectAdapterChecks(config.projectRoot, project)) {
      steps.push(runShell(check.name, check.command, config.projectRoot, timeout));
      if (steps.some((s) => s.status === "failed")) {
        return finalize(steps, start);
      }
    }
  }

  // 4. Drift check — always (unless skipped)
  if (!opts.skipDrift) {
    const driftStep = await runDriftStep(config);
    steps.push(driftStep);
  }

  // 5. Test suite — opt-in (--full). Highest signal, slowest; runs last so the
  // cheaper checks above fail fast first. Stays out of the default Stop-hook path.
  if (opts.runTests) {
    for (const check of detectTestChecks(config.projectRoot, project)) {
      steps.push(runShell(check.name, check.command, config.projectRoot, timeout));
    }
  }

  return finalize(steps, start);
}

function finalize(steps: VerifyStep[], startMs: number): VerifyResult {
  return {
    passed: steps.every((s) => s.status !== "failed"),
    steps,
    totalDurationMs: Date.now() - startMs,
  };
}

async function runTypecheck(projectRoot: string, project: ProjectModel, timeoutMs: number): Promise<VerifyStep | null> {
  // Only Node.js projects with a tsconfig.json get a typecheck step
  if (project.rootManifest?.type !== "package.json") return null;
  if (!existsSync(join(projectRoot, "tsconfig.json"))) return null;

  const scripts = project.rootManifest.scripts ?? {};
  // Prefer an explicit typecheck script if it exists
  if (scripts.typecheck) {
    return runShell("typecheck", "npm run typecheck", projectRoot, timeoutMs);
  }
  if (scripts["type-check"]) {
    return runShell("typecheck", "npm run type-check", projectRoot, timeoutMs);
  }
  // Fall back to direct tsc invocation
  return runShell("typecheck", "npx tsc --noEmit", projectRoot, timeoutMs);
}

async function runBuild(projectRoot: string, project: ProjectModel, timeoutMs: number): Promise<VerifyStep | null> {
  if (project.rootManifest?.type !== "package.json") return null;
  const scripts = project.rootManifest.scripts ?? {};
  if (!scripts.build) return null;
  return runShell("build", "npm run build", projectRoot, timeoutMs);
}

export function detectAdapterChecks(projectRoot: string, project: ProjectModel): AdapterCheck[] {
  const manifest = project.rootManifest;
  if (!manifest) return [];

  switch (manifest.type) {
    case "package.json":
      return detectNodeChecks(manifest);
    case "pyproject.toml":
    case "requirements.txt":
      return detectPythonChecks(projectRoot, manifest);
    case "go.mod":
      // Default: compile-level check only (fast). The full `go test ./...` moves
      // behind --full via detectTestChecks — see the v3.3 behavior-change note.
      return hasSourceFile(projectRoot, "**/*.go")
        ? [{ name: "go:build", command: "go build ./..." }]
        : [];
    case "Cargo.toml":
      return [{ name: "rust:check", command: "cargo check" }];
    case "pom.xml":
      return [{ name: "java:compile", command: detectMavenCommand(projectRoot) }];
    case "build.gradle":
    case "build.gradle.kts":
      return [{ name: "java:compile", command: detectGradleCommand(projectRoot) }];
    case "Gemfile":
      return detectRubyChecks(projectRoot, manifest);
  }
}

/**
 * Detect the test command for the project's stack. Same deterministic, manifest-driven
 * detection as the adapters — only returns a step when the project actually has tests.
 * Run only under `cai verify --full` (or `runTests: true`), never in the fast default path.
 */
export function detectTestChecks(projectRoot: string, project: ProjectModel): AdapterCheck[] {
  const manifest = project.rootManifest;
  if (!manifest) return [];

  switch (manifest.type) {
    case "package.json": {
      const scripts = manifest.scripts ?? {};
      const test = scripts.test;
      // Skip the npm-init placeholder ("no test specified" / bare `node --test` with no files).
      if (!test) return [];
      if (/no test specified/.test(test)) return [];
      return [{ name: "test", command: "npm test" }];
    }
    case "pyproject.toml":
    case "requirements.txt":
      return hasDependency(manifest, "pytest") || hasSourceFile(projectRoot, "**/test_*.py") || hasSourceFile(projectRoot, "**/*_test.py")
        ? [{ name: "python:pytest", command: "python -m pytest" }]
        : [];
    case "go.mod":
      return hasSourceFile(projectRoot, "**/*.go")
        ? [{ name: "go:test", command: "go test ./..." }]
        : [];
    case "Cargo.toml":
      return [{ name: "rust:test", command: "cargo test" }];
    case "pom.xml":
      return [{ name: "java:test", command: existsSync(join(projectRoot, "mvnw")) ? "./mvnw -q test" : "mvn -q test" }];
    case "build.gradle":
    case "build.gradle.kts":
      return [{ name: "java:test", command: existsSync(join(projectRoot, "gradlew")) ? "./gradlew test" : "gradle test" }];
    case "Gemfile":
      return hasDependency(manifest, "rspec") ? [{ name: "ruby:rspec", command: "bundle exec rspec" }] : [];
  }
}

function detectNodeChecks(manifest: ManifestInfo): AdapterCheck[] {
  const scripts = manifest.scripts ?? {};
  const checks: AdapterCheck[] = [];

  if (scripts.lint) {
    checks.push({ name: "lint", command: "npm run lint" });
  }
  if (scripts["react-doctor"]) {
    checks.push({ name: "react-doctor", command: "npm run react-doctor" });
  }

  return checks;
}

function detectPythonChecks(projectRoot: string, manifest: ManifestInfo): AdapterCheck[] {
  const checks: AdapterCheck[] = [];

  if (
    hasDependency(manifest, "ruff") ||
    existsAny(projectRoot, ["ruff.toml", ".ruff.toml"]) ||
    pyprojectHasSection(projectRoot, "tool.ruff")
  ) {
    checks.push({ name: "python:ruff", command: "python -m ruff check ." });
  }
  if (
    hasDependency(manifest, "mypy") ||
    existsAny(projectRoot, ["mypy.ini", ".mypy.ini"]) ||
    pyprojectHasSection(projectRoot, "tool.mypy")
  ) {
    checks.push({ name: "python:mypy", command: "python -m mypy ." });
  }

  return checks;
}

function detectRubyChecks(projectRoot: string, manifest: ManifestInfo): AdapterCheck[] {
  if (hasDependency(manifest, "rubocop") || existsAny(projectRoot, [".rubocop.yml"])) {
    return [{ name: "ruby:rubocop", command: "bundle exec rubocop" }];
  }
  return [];
}

function detectMavenCommand(projectRoot: string): string {
  return existsSync(join(projectRoot, "mvnw"))
    ? "./mvnw -q -DskipTests compile"
    : "mvn -q -DskipTests compile";
}

function detectGradleCommand(projectRoot: string): string {
  return existsSync(join(projectRoot, "gradlew"))
    ? "./gradlew classes"
    : "gradle classes";
}

function hasDependency(manifest: ManifestInfo, name: string): boolean {
  return Boolean(manifest.dependencies[name] || manifest.devDependencies[name]);
}

function existsAny(projectRoot: string, paths: string[]): boolean {
  return paths.some((path) => existsSync(join(projectRoot, path)));
}

function hasSourceFile(projectRoot: string, pattern: string): boolean {
  return globSync(pattern, {
    cwd: projectRoot,
    ignore: ["**/node_modules/**", "**/dist/**", "**/target/**", "**/.git/**"],
    nodir: true,
  }).length > 0;
}

function pyprojectHasSection(projectRoot: string, section: string): boolean {
  const path = join(projectRoot, "pyproject.toml");
  if (!existsSync(path)) return false;
  try {
    const content = readFileSync(path, "utf-8");
    return new RegExp(`^\\[${section.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\]`, "m").test(content);
  } catch {
    return false;
  }
}

async function runDriftStep(config: CaiConfig): Promise<VerifyStep> {
  const stepStart = Date.now();
  try {
    // Use --fast equivalent: skip the staleness checker (slow due to git ops)
    const report = await runDriftCheck(config, { skip: ["staleness"] });
    const errors = report.issues.filter((i) => i.severity === "error").length;
    const status: VerifyStep["status"] = errors > 0 ? "failed" : "passed";
    const summary = errors > 0
      ? `${errors} error${errors !== 1 ? "s" : ""} (score ${report.score}/100)`
      : `clean (score ${report.score}/100)`;
    return {
      name: "drift",
      command: "cai check --skip staleness",
      status,
      durationMs: Date.now() - stepStart,
      output: summary,
    };
  } catch (err) {
    return {
      name: "drift",
      command: "cai check --skip staleness",
      status: "failed",
      durationMs: Date.now() - stepStart,
      output: (err as Error).message,
    };
  }
}

function runShell(name: string, command: string, cwd: string, timeoutMs: number): VerifyStep {
  const stepStart = Date.now();
  // Split the command into argv. Keep it simple — these commands are all
  // generated by us, no shell metachars to worry about.
  const parts = command.split(/\s+/);
  const result = spawnSync(parts[0], parts.slice(1), {
    cwd,
    encoding: "utf8",
    timeout: timeoutMs,
    stdio: ["ignore", "pipe", "pipe"],
    maxBuffer: 4 * 1024 * 1024,
  });

  if (result.error) {
    const code = (result.error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      return {
        name,
        command,
        status: "skipped",
        durationMs: Date.now() - stepStart,
        reason: `${parts[0]} not found in PATH`,
      };
    }
    return {
      name,
      command,
      status: "failed",
      durationMs: Date.now() - stepStart,
      output: result.error.message,
    };
  }

  // Combine stdout and stderr for the agent's context — both can carry useful info
  const combined = ((result.stdout ?? "") + "\n" + (result.stderr ?? "")).trim();

  if (result.status === 0) {
    return {
      name,
      command,
      status: "passed",
      durationMs: Date.now() - stepStart,
    };
  }

  return {
    name,
    command,
    status: "failed",
    durationMs: Date.now() - stepStart,
    output: truncate(combined, 4000),
  };
}

function truncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars) + `\n… (truncated, ${text.length - maxChars} more chars)`;
}

/** Pretty-print the verify result for humans. */
export function printVerifyResult(result: VerifyResult): void {
  console.log();
  console.log(chalk.bold("  cai verify"));
  console.log();

  for (const step of result.steps) {
    const icon = step.status === "passed" ? chalk.green("✔")
      : step.status === "failed" ? chalk.red("✖")
      : chalk.dim("○");
    const duration = chalk.dim(`(${(step.durationMs / 1000).toFixed(1)}s)`);

    console.log(`  ${icon} ${chalk.bold(step.name.padEnd(10))}  ${chalk.dim(step.command)}  ${duration}`);

    if (step.status === "skipped" && step.reason) {
      console.log(`            ${chalk.dim(step.reason)}`);
    }
    if (step.status === "passed" && step.output) {
      console.log(`            ${chalk.dim(step.output)}`);
    }
    if (step.status === "failed" && step.output) {
      const lines = step.output.split("\n").slice(0, 12);
      for (const line of lines) {
        console.log(`            ${chalk.dim(line)}`);
      }
      if (step.output.split("\n").length > 12) {
        console.log(`            ${chalk.dim("…")}`);
      }
    }
  }

  console.log();
  if (result.passed) {
    console.log(`  ${chalk.green("✓ all checks passed")}  ${chalk.dim(`(${(result.totalDurationMs / 1000).toFixed(1)}s total)`)}`);
  } else {
    const failed = result.steps.filter((s) => s.status === "failed").map((s) => s.name).join(", ");
    console.log(`  ${chalk.red("✖ failed:")} ${chalk.bold(failed)}  ${chalk.dim(`(${(result.totalDurationMs / 1000).toFixed(1)}s total)`)}`);
  }
  console.log();
}
